import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import ReviewCycleWidget from '../components/ReviewCycleWidget';
import { track } from '../utils/analytics';
import { DELIVERY_ENABLED } from '../utils/features';

import { CANVAS_API, API_BASE } from '../config';
const API = CANVAS_API;
const DELIVERY_API = `${API_BASE}/delivery`;
const STORAGE_KEY = 'guruone_strategy';
const STORAGE_KEY_OM = 'guruone_operating_model';
const INITIATIVE_STALE_DAYS = 90;
const INITIATIVE_DUE_SOON_DAYS = 30;

// Maps a saved-initiative count to a Delivery track-dot percentage.
// 0 → 0%, 1–2 → 25%, 3–5 → 50%, 6–9 → 75%, 10+ → 100%.
function calcDeliveryPct(initiativeCount) {
  if (!initiativeCount || initiativeCount <= 0) return 0;
  if (initiativeCount <= 2) return 25;
  if (initiativeCount <= 5) return 50;
  if (initiativeCount <= 9) return 75;
  return 100;
}

// ── Data helpers ──────────────────────────────────────────────────
function loadStrategyData(userId) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const all = JSON.parse(raw);
    const key = userId ? `user_${userId}` : null;
    if (key && all[key]) return all[key];
    const entries = Object.values(all).filter(v => typeof v === 'object' && v !== null);
    return entries.reduce((best, curr) =>
      Object.keys(curr).length > Object.keys(best).length ? curr : best, {});
  } catch { return {}; }
}

function calcStrategyPct(data) {
  const KEYS = ['founder_purpose','values','mission','vision','value_proposition',
    'customers','competitors','differentiators','go_to_market','revenue_model',
    'cost_model','current_challenges'];
  const filled = KEYS.filter(k => {
    const s = data[k];
    if (!s) return false;
    if (s.entries) return s.entries.some(e => Object.values(e).some(v => v));
    return Object.values(s).some(v => v && (Array.isArray(v) ? v.length > 0 : String(v).trim().length > 0));
  }).length;
  return Math.round((filled / KEYS.length) * 100);
}

function calcBlueprintPct() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_OM);
    if (!raw) return 0;
    const data = JSON.parse(raw);
    const sections = Object.values(data).filter(v => typeof v === 'object' && v !== null);
    const filled = sections.filter(s => s.current &&
      Object.values(s.current).some(v => {
        if (!v) return false;
        if (Array.isArray(v)) return v.length > 0;
        if (typeof v === 'object') return false;
        return String(v).trim().length > 0;
      })).length;
    return Math.round((filled / 10) * 100);
  } catch { return 0; }
}

function getDecisionCount() {
  try { return (JSON.parse(localStorage.getItem('guruone_decisions')) || []).length; } catch { return 0; }
}

function getChallenges() {
  try { return (JSON.parse(localStorage.getItem('guruone_challenges')) || []).filter(c => c && c.trim()); }
  catch { return []; }
}

// ── SVG Icons ─────────────────────────────────────────────────────

function DirectionIcon() {
  return (
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
      <rect x="10" y="26" width="60" height="44" rx="6" fill="#F3F0FF" stroke="#7C3AED" strokeWidth="1.5"/>
      <line x1="10" y1="42" x2="70" y2="42" stroke="#7C3AED" strokeWidth="1" strokeDasharray="4 3" opacity="0.35"/>
      <line x1="10" y1="56" x2="70" y2="56" stroke="#7C3AED" strokeWidth="1" strokeDasharray="4 3" opacity="0.35"/>
      <line x1="40" y1="42" x2="40" y2="70" stroke="#7C3AED" strokeWidth="1" strokeDasharray="4 3" opacity="0.35"/>
      <rect x="10" y="26" width="60" height="16" rx="6" fill="#7C3AED" opacity="0.14"/>
      <rect x="17" y="32" width="20" height="6" rx="3" fill="#7C3AED" opacity="0.55"/>
      <rect x="42" y="32" width="22" height="6" rx="3" fill="#7C3AED" opacity="0.3"/>
      <rect x="15" y="47" width="20" height="4" rx="2" fill="#7C3AED" opacity="0.4"/>
      <rect x="15" y="60" width="18" height="4" rx="2" fill="#7C3AED" opacity="0.4"/>
      <rect x="44" y="47" width="18" height="4" rx="2" fill="#7C3AED" opacity="0.4"/>
      <rect x="44" y="60" width="18" height="4" rx="2" fill="#7C3AED" opacity="0.4"/>
      <circle cx="61" cy="16" r="11" fill="white" stroke="#7C3AED" strokeWidth="1.5"/>
      <path d="M61 7 L62.5 14.5 L61 13 L59.5 14.5 Z" fill="#7C3AED"/>
      <path d="M61 25 L59.5 17.5 L61 19 L62.5 17.5 Z" fill="#7C3AED" opacity="0.4"/>
      <path d="M52 16 L59.5 14.5 L58 16 L59.5 17.5 Z" fill="#7C3AED" opacity="0.4"/>
      <path d="M70 16 L62.5 17.5 L64 16 L62.5 14.5 Z" fill="#7C3AED"/>
      <circle cx="61" cy="16" r="2.5" fill="#7C3AED"/>
    </svg>
  );
}

function DecisionIcon() {
  return (
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
      <line x1="40" y1="10" x2="40" y2="36" stroke="#1D9E75" strokeWidth="2.5" strokeLinecap="round"/>
      <circle cx="40" cy="9" r="4" fill="#1D9E75"/>
      <line x1="12" y1="28" x2="68" y2="28" stroke="#1D9E75" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="14" y1="28" x2="10" y2="44" stroke="#1D9E75" strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="14" y1="28" x2="26" y2="44" stroke="#1D9E75" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M6 44 Q18 49 30 44" stroke="#1D9E75" strokeWidth="2" fill="none" strokeLinecap="round"/>
      <rect x="10" y="36" width="7" height="5" rx="2" fill="#1D9E75" opacity="0.25"/>
      <rect x="19" y="38" width="5" height="3" rx="1.5" fill="#1D9E75" opacity="0.45"/>
      <line x1="66" y1="28" x2="62" y2="46" stroke="#1D9E75" strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="66" y1="28" x2="78" y2="46" stroke="#1D9E75" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M58 46 Q70 51 82 46" stroke="#1D9E75" strokeWidth="2" fill="none" strokeLinecap="round"/>
      <rect x="62" y="37" width="7" height="5" rx="2" fill="#1D9E75" opacity="0.45"/>
      <rect x="71" y="39" width="5" height="3" rx="1.5" fill="#1D9E75" opacity="0.25"/>
      <circle cx="73" cy="32" r="7" fill="#E1F5EE" stroke="#1D9E75" strokeWidth="1.5"/>
      <path d="M73 27 L74.5 31 L79 31 L75.5 33.5 L77 37.5 L73 35 L69 37.5 L70.5 33.5 L67 31 L71.5 31 Z" fill="#1D9E75" opacity="0.7"/>
      <line x1="40" y1="36" x2="40" y2="58" stroke="#1D9E75" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="30" y1="58" x2="50" y2="58" stroke="#1D9E75" strokeWidth="2.5" strokeLinecap="round"/>
      <rect x="14" y="63" width="15" height="6" rx="3" fill="#1D9E75" opacity="0.12" stroke="#1D9E75" strokeWidth="1"/>
      <rect x="32" y="63" width="15" height="6" rx="3" fill="#1D9E75" opacity="0.12" stroke="#1D9E75" strokeWidth="1"/>
      <rect x="50" y="63" width="15" height="6" rx="3" fill="#1D9E75" opacity="0.3" stroke="#1D9E75" strokeWidth="1"/>
    </svg>
  );
}

function DesignIcon() {
  return (
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
      <rect x="8" y="12" width="64" height="56" rx="5" fill="#185FA5" fillOpacity="0.07" stroke="#185FA5" strokeWidth="1.5"/>
      {[22, 34, 46, 58].map(y => (
        <line key={y} x1="8" y1={y} x2="72" y2={y} stroke="#185FA5" strokeWidth="0.5" opacity="0.2"/>
      ))}
      {[22, 36, 50, 64].map(x => (
        <line key={x} x1={x} y1="12" x2={x} y2="68" stroke="#185FA5" strokeWidth="0.5" opacity="0.2"/>
      ))}
      <rect x="30" y="18" width="20" height="12" rx="3" fill="#185FA5" fillOpacity="0.25" stroke="#185FA5" strokeWidth="1.5"/>
      <line x1="36" y1="22" x2="44" y2="22" stroke="#185FA5" strokeWidth="1" opacity="0.6"/>
      <line x1="36" y1="25" x2="44" y2="25" stroke="#185FA5" strokeWidth="1" opacity="0.4"/>
      <line x1="40" y1="30" x2="40" y2="38" stroke="#185FA5" strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="22" y1="38" x2="58" y2="38" stroke="#185FA5" strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="22" y1="38" x2="22" y2="44" stroke="#185FA5" strokeWidth="1.5" strokeLinecap="round"/>
      <rect x="12" y="44" width="20" height="10" rx="3" fill="#185FA5" fillOpacity="0.2" stroke="#185FA5" strokeWidth="1.2"/>
      <line x1="16" y1="48" x2="28" y2="48" stroke="#185FA5" strokeWidth="1" opacity="0.5"/>
      <line x1="40" y1="38" x2="40" y2="44" stroke="#185FA5" strokeWidth="1.5" strokeLinecap="round"/>
      <rect x="30" y="44" width="20" height="10" rx="3" fill="#185FA5" fillOpacity="0.2" stroke="#185FA5" strokeWidth="1.2"/>
      <line x1="34" y1="48" x2="46" y2="48" stroke="#185FA5" strokeWidth="1" opacity="0.5"/>
      <line x1="58" y1="38" x2="58" y2="44" stroke="#185FA5" strokeWidth="1.5" strokeLinecap="round"/>
      <rect x="48" y="44" width="20" height="10" rx="3" fill="#185FA5" fillOpacity="0.2" stroke="#185FA5" strokeWidth="1.2"/>
      <line x1="52" y1="48" x2="64" y2="48" stroke="#185FA5" strokeWidth="1" opacity="0.5"/>
      <rect x="12" y="58" width="13" height="7" rx="2" fill="#185FA5" opacity="0.15" stroke="#185FA5" strokeWidth="1"/>
      <text x="18.5" y="63.5" textAnchor="middle" fontSize="5" fill="#185FA5" fontWeight="600">P1</text>
      <path d="M25.5 61.5 L28.5 61.5" stroke="#185FA5" strokeWidth="1.2" strokeLinecap="round"/>
      <path d="M27 60 L29 61.5 L27 63" stroke="#185FA5" strokeWidth="1" fill="none" strokeLinecap="round"/>
      <rect x="30" y="58" width="13" height="7" rx="2" fill="#185FA5" opacity="0.15" stroke="#185FA5" strokeWidth="1"/>
      <text x="36.5" y="63.5" textAnchor="middle" fontSize="5" fill="#185FA5" fontWeight="600">P2</text>
      <path d="M43.5 61.5 L46.5 61.5" stroke="#185FA5" strokeWidth="1.2" strokeLinecap="round"/>
      <path d="M45 60 L47 61.5 L45 63" stroke="#185FA5" strokeWidth="1" fill="none" strokeLinecap="round"/>
      <rect x="48" y="58" width="13" height="7" rx="2" fill="#185FA5" opacity="0.3" stroke="#185FA5" strokeWidth="1.2"/>
      <text x="54.5" y="63.5" textAnchor="middle" fontSize="5" fill="#185FA5" fontWeight="700">P3</text>
    </svg>
  );
}

function DeliveryIcon() {
  return (
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
      <rect x="10" y="36" width="60" height="12" rx="6" fill="#FEF3C7" stroke="#D97706" strokeWidth="1.5"/>
      <rect x="10" y="36" width="32" height="12" rx="6" fill="#D97706" opacity="0.2"/>
      <circle cx="24" cy="42" r="9" fill="white" stroke="#D97706" strokeWidth="2"/>
      <path d="M21 42 L23 44 L27 40" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="46" cy="42" r="9" fill="white" stroke="#D97706" strokeWidth="2"/>
      <path d="M43 42 L45 44 L49 40" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="68" cy="42" r="9" fill="#FEF3C7" stroke="#D97706" strokeWidth="2" strokeDasharray="3 2"/>
      <text x="68" y="46" textAnchor="middle" fontSize="8" fill="#D97706" fontWeight="bold">3</text>
      <rect x="14" y="18" width="18" height="10" rx="4" fill="#D97706" opacity="0.18"/>
      <rect x="37" y="18" width="18" height="10" rx="4" fill="#D97706" opacity="0.18"/>
      <rect x="60" y="18" width="18" height="10" rx="4" fill="#D97706" opacity="0.08"/>
      <line x1="23" y1="28" x2="23" y2="33" stroke="#D97706" strokeWidth="1.5" strokeDasharray="2 2" opacity="0.5"/>
      <line x1="46" y1="28" x2="46" y2="33" stroke="#D97706" strokeWidth="1.5" strokeDasharray="2 2" opacity="0.5"/>
      <line x1="69" y1="28" x2="69" y2="33" stroke="#D97706" strokeWidth="1.5" strokeDasharray="2 2" opacity="0.3"/>
      <rect x="12" y="54" width="20" height="5" rx="2" fill="#D97706" opacity="0.3"/>
      <rect x="12" y="62" width="15" height="4" rx="2" fill="#D97706" opacity="0.2"/>
      <rect x="36" y="54" width="20" height="5" rx="2" fill="#D97706" opacity="0.3"/>
      <rect x="36" y="62" width="15" height="4" rx="2" fill="#D97706" opacity="0.2"/>
      <rect x="60" y="54" width="18" height="5" rx="2" fill="#D97706" opacity="0.12"/>
      <rect x="60" y="62" width="14" height="4" rx="2" fill="#D97706" opacity="0.08"/>
    </svg>
  );
}

function FinanceIcon() {
  return (
    <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
      <rect x="6" y="28" width="8" height="20" rx="2" fill="#BA7517" opacity="0.25" stroke="#BA7517" strokeWidth="1.2"/>
      <rect x="18" y="20" width="8" height="28" rx="2" fill="#BA7517" opacity="0.4" stroke="#BA7517" strokeWidth="1.2"/>
      <rect x="30" y="12" width="8" height="36" rx="2" fill="#BA7517" opacity="0.6" stroke="#BA7517" strokeWidth="1.2"/>
      <rect x="42" y="6" width="8" height="42" rx="2" fill="#BA7517" opacity="0.85" stroke="#BA7517" strokeWidth="1.2"/>
      <line x1="6" y1="50" x2="52" y2="50" stroke="#BA7517" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M8 26 L20 18 L32 10 L44 4" stroke="#BA7517" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="3 2" opacity="0.5"/>
    </svg>
  );
}

function MarketingIcon() {
  return (
    <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
      <path d="M10 28 C10 28 18 16 28 16 C38 16 46 28 46 28 C46 28 38 40 28 40 C18 40 10 28 10 28Z" stroke="#7B3AC2" strokeWidth="1.5" fill="#7B3AC2" fillOpacity="0.08"/>
      <circle cx="28" cy="28" r="8" stroke="#7B3AC2" strokeWidth="1.5" fill="#7B3AC2" fillOpacity="0.18"/>
      <circle cx="28" cy="28" r="3.5" fill="#7B3AC2"/>
      <line x1="36" y1="20" x2="44" y2="12" stroke="#7B3AC2" strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="46" cy="10" r="4" fill="#7B3AC2" opacity="0.7"/>
      <line x1="28" y1="8" x2="28" y2="4" stroke="#7B3AC2" strokeWidth="1.2" strokeLinecap="round" opacity="0.4"/>
      <line x1="46" y1="28" x2="50" y2="28" stroke="#7B3AC2" strokeWidth="1.2" strokeLinecap="round" opacity="0.4"/>
      <line x1="10" y1="28" x2="6" y2="28" stroke="#7B3AC2" strokeWidth="1.2" strokeLinecap="round" opacity="0.4"/>
    </svg>
  );
}

function TechnologyIcon() {
  return (
    <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
      <rect x="6" y="10" width="36" height="26" rx="3" fill="#0891B2" fillOpacity="0.1" stroke="#0891B2" strokeWidth="1.5"/>
      <rect x="9" y="13" width="30" height="18" rx="1.5" fill="#0891B2" fillOpacity="0.08"/>
      <rect x="12" y="16" width="10" height="2.5" rx="1.25" fill="#0891B2" opacity="0.6"/>
      <rect x="12" y="21" width="16" height="2.5" rx="1.25" fill="#0891B2" opacity="0.35"/>
      <rect x="12" y="26" width="12" height="2.5" rx="1.25" fill="#0891B2" opacity="0.5"/>
      <rect x="26" y="16" width="8" height="2.5" rx="1.25" fill="#0891B2" opacity="0.25"/>
      <line x1="24" y1="36" x2="24" y2="43" stroke="#0891B2" strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="18" y1="43" x2="30" y2="43" stroke="#0891B2" strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="47" cy="14" r="3" fill="#0891B2" opacity="0.7"/>
      <circle cx="47" cy="26" r="3" fill="#0891B2" opacity="0.4"/>
      <circle cx="47" cy="38" r="3" fill="#0891B2" opacity="0.25"/>
      <line x1="47" y1="17" x2="47" y2="23" stroke="#0891B2" strokeWidth="1.2" opacity="0.5"/>
      <line x1="47" y1="29" x2="47" y2="35" stroke="#0891B2" strokeWidth="1.2" opacity="0.35"/>
      <line x1="44" y1="14" x2="42" y2="14" stroke="#0891B2" strokeWidth="1.2" opacity="0.4"/>
      <line x1="44" y1="26" x2="42" y2="26" stroke="#0891B2" strokeWidth="1.2" opacity="0.3"/>
      <path d="M36 48 Q39 45 42 48" stroke="#0891B2" strokeWidth="1.5" fill="none" strokeLinecap="round" opacity="0.6"/>
      <path d="M33 51 Q39 44 45 51" stroke="#0891B2" strokeWidth="1.2" fill="none" strokeLinecap="round" opacity="0.3"/>
      <circle cx="39" cy="50" r="1.5" fill="#0891B2" opacity="0.7"/>
    </svg>
  );
}

function SalesIcon() {
  return (
    <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
      <path d="M8 10 L48 10 L34 26 L34 44 L22 38 L22 26 Z" fill="#C0442B" fillOpacity="0.1" stroke="#C0442B" strokeWidth="1.5" strokeLinejoin="round"/>
      <line x1="8" y1="10" x2="48" y2="10" stroke="#C0442B" strokeWidth="2" strokeLinecap="round"/>
      <line x1="13" y1="17" x2="43" y2="17" stroke="#C0442B" strokeWidth="1" opacity="0.35" strokeDasharray="3 2"/>
      <line x1="18" y1="23" x2="38" y2="23" stroke="#C0442B" strokeWidth="1" opacity="0.25" strokeDasharray="3 2"/>
      <path d="M36 50 L48 40" stroke="#C0442B" strokeWidth="1.8" strokeLinecap="round"/>
      <path d="M44 38 L48 40 L46 44" stroke="#C0442B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function PeopleIcon() {
  return (
    <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
      <circle cx="28" cy="14" r="7" fill="#0D9488" fillOpacity="0.2" stroke="#0D9488" strokeWidth="1.5"/>
      <path d="M16 36 Q16 26 28 26 Q40 26 40 36" fill="#0D9488" fillOpacity="0.12" stroke="#0D9488" strokeWidth="1.5"/>
      <circle cx="11" cy="22" r="5" fill="#0D9488" fillOpacity="0.15" stroke="#0D9488" strokeWidth="1.2"/>
      <path d="M3 38 Q3 30 11 30 Q19 30 19 38" fill="#0D9488" fillOpacity="0.08" stroke="#0D9488" strokeWidth="1.2" opacity="0.65"/>
      <circle cx="45" cy="22" r="5" fill="#0D9488" fillOpacity="0.15" stroke="#0D9488" strokeWidth="1.2"/>
      <path d="M37 38 Q37 30 45 30 Q53 30 53 38" fill="#0D9488" fillOpacity="0.08" stroke="#0D9488" strokeWidth="1.2" opacity="0.65"/>
      <line x1="17" y1="23" x2="22" y2="25" stroke="#0D9488" strokeWidth="1" opacity="0.4" strokeDasharray="2 2"/>
      <line x1="39" y1="23" x2="34" y2="25" stroke="#0D9488" strokeWidth="1" opacity="0.4" strokeDasharray="2 2"/>
      <rect x="8" y="46" width="40" height="4" rx="2" fill="#0D9488" opacity="0.15"/>
      <rect x="18" y="46" width="20" height="4" rx="2" fill="#0D9488" opacity="0.3"/>
    </svg>
  );
}

// ── Progress ring ─────────────────────────────────────────────────
function ProgressRing({ pct, colour, size = 36 }) {
  const r = size / 2 - 3;
  const circ = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={colour + '22'} strokeWidth="3"/>
      {pct > 0 && (
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={colour} strokeWidth="3"
          strokeDasharray={`${(pct/100)*circ} ${circ}`} strokeLinecap="round"
          transform={`rotate(-90 ${size/2} ${size/2})`}/>
      )}
      <text x={size/2} y={size/2 + 4} textAnchor="middle" fontSize="9" fontWeight="700"
        fill={pct > 0 ? colour : colour + '55'}>{pct}%</text>
    </svg>
  );
}

// ── Got 5 Mins ────────────────────────────────────────────────────
function Got5MinsTrigger({ onClick }) {
  return (
    <button onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '0.5rem',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        border: 'none', borderRadius: 10,
        padding: '0.55rem 1rem', cursor: 'pointer', fontFamily: 'inherit',
        boxShadow: '0 2px 8px rgba(0,0,0,0.18)', flexShrink: 0, width: 'auto',
      }}>
      <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#1D9E75', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', flexShrink: 0 }}>⚡</div>
      <div style={{ textAlign: 'left' }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'white', lineHeight: 1 }}>Only got 5 mins?</div>
        <div style={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>Where to focus right now</div>
      </div>
    </button>
  );
}

function Got5Mins({ strategyData, challenges, sPct, bPct, initiativeStats, open, setOpen }) {
  const [loading, setLoading] = useState(false);
  const [advice, setAdvice] = useState(null);

  async function getAdvice() {
    if (advice || loading) return;
    setLoading(true);
    const KEYS = ['founder_purpose','values','mission','vision','value_proposition',
      'customers','competitors','differentiators','go_to_market','revenue_model',
      'cost_model','current_challenges'];
    const filled = KEYS.filter(k => {
      const s = strategyData[k];
      if (!s) return false;
      if (s.entries) return s.entries.some(e => Object.values(e).some(v => v));
      return Object.values(s).some(v => v && (Array.isArray(v) ? v.length > 0 : String(v).trim().length > 0));
    });
    const missing = KEYS.filter(k => !filled.includes(k));
    const challengeList = challenges.join('; ') || 'none noted';

    const systemPrompt = `You are GuruOne — a virtual C-suite for SME leaders. Be direct, warm, practical.
The platform has a strategy journey: Direction Guru (Chief Strategy Officer), Design Guru (Chief Operating Officer), Delivery Guru (Chief of Staff).
Cross-cutting advisors available at any stage: Decision Guru (Non-Executive Director).

The user has TOLD YOU they have only 5 minutes. Recommend EXACTLY ONE action that can realistically be completed in 5 minutes or less.

WHAT COUNTS AS A 5-MINUTE ACTION:
- Fill in ONE specific Direction or Design field (name it specifically — e.g. "fill in Vision in Direction Guru" not "complete the Direction canvas")
- Answer ONE specific question or chip selection
- Status-update ONE specific overdue initiative
- Capture ONE current challenge
- Note ONE risk on a single Design Guru card
- Write a one-paragraph note in a single section

WHAT DOES NOT COUNT (DO NOT RECOMMEND):
- "Complete the Direction canvas" or any whole-canvas action (these take 30+ minutes)
- "Run a deep-dive dialogue" (these take 10+ minutes)
- "Map your operating model" (much too big)
- Anything described as a journey or process

Choose the action that gives the highest leverage given what's missing. Be specific about which field, which initiative, which question.

Respond ONLY with JSON: { "headline": "one sentence framing the focus", "action": "the specific 5-minute task — name the exact field or item", "why": "one sentence on why this is the highest-leverage 5 minutes", "guru": "Direction Guru | Decision Guru | Design Guru | Delivery Guru" }`;

    const stats = initiativeStats || { total: 0, overdue: 0, dueSoon: 0, stale: 0, done: 0 };
    const initiativeLine = stats.total === 0
      ? 'Initiative health: no initiatives saved yet.'
      : `Initiative health: ${stats.total} saved. ${stats.overdue} overdue, ${stats.dueSoon} due within ${INITIATIVE_DUE_SOON_DAYS} days, ${stats.stale} not status-updated in ${INITIATIVE_STALE_DAYS}+ days, ${stats.done} marked done.`;

    const userPrompt = `Direction canvas: ${sPct}% complete. Design canvas: ${bPct}% complete.
Direction canvas filled sections: ${filled.map(k => k.replace(/_/g,' ')).join(', ') || 'none'}.
Missing: ${missing.map(k => k.replace(/_/g,' ')).join(', ')}.
Challenges: ${challengeList}.
${initiativeLine}
What should this leader focus on right now?`;

    try {
      const res = await axios.post(`${API}/chat`, { messages: [{ role: 'user', content: userPrompt }], context: systemPrompt });
      const raw = res.data.reply;
      const s = raw.indexOf('{'); const e = raw.lastIndexOf('}');
      if (s !== -1) setAdvice(JSON.parse(raw.slice(s, e + 1)));
    } catch {
      // Pick a sensible single 5-min task based on what's most clearly missing.
      const fallback = !filled.includes('vision')
        ? { action: 'Fill in your Vision in Direction Guru', why: 'A clear vision shapes everything else GuruOne suggests.', guru: 'Direction Guru' }
        : !filled.includes('current_challenges')
        ? { action: 'Note your single biggest challenge in Direction Guru', why: 'Helps GuruOne sharpen every recommendation it gives you.', guru: 'Direction Guru' }
        : { action: 'Open Design Guru and pick one section to start on', why: 'Five minutes on any single Design card moves the picture forward.', guru: 'Design Guru' };
      setAdvice({
        headline: "Here's the highest-leverage thing you can do in 5 minutes.",
        ...fallback,
      });
    }
    setLoading(false);
  }

  const guruColour = { 'Direction Guru': '#7C3AED', 'Decision Guru': '#48B650', 'Design Guru': '#185FA5', 'Delivery Guru': '#D97706' };

  useEffect(() => {
    if (open) getAdvice();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  return (
    <div style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #0f2744 100%)', borderRadius: 12, padding: '1.25rem 1.5rem', boxShadow: '0 2px 16px rgba(0,0,0,0.15)', marginBottom: '1.75rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#1D9E75', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>⚡</div>
          <div>
            <div style={{ fontSize: '0.88rem', fontWeight: 700, color: 'white' }}>Your priority focus</div>
            {advice && <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>"{advice.headline}"</div>}
          </div>
        </div>
        <button onClick={() => setOpen(false)}
          style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.35)', fontSize: '0.8rem', cursor: 'pointer', width: 'auto', fontFamily: 'inherit' }}>✕</button>
      </div>

      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem 0' }}>
          <div style={{ width: 18, height: 18, border: '2px solid rgba(255,255,255,0.1)', borderTop: '2px solid #1D9E75', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)' }}>Consulting your virtual C-suite…</span>
        </div>
      )}

      {advice && advice.action && (() => {
        const col = guruColour[advice.guru] || '#1D9E75';
        return (
          <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 10, padding: '1rem 1.15rem', borderTop: `3px solid ${col}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', marginBottom: '0.55rem' }}>
              <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '2px 9px', borderRadius: 20, background: col + '30', color: col }}>→ {advice.guru}</span>
              <span style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.45)', fontStyle: 'italic' }}>~5 minutes</span>
            </div>
            <div style={{ fontSize: '0.9rem', fontWeight: 700, color: 'white', lineHeight: 1.4, marginBottom: '0.35rem' }}>{advice.action}</div>
            <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>{advice.why}</div>
          </div>
        );
      })()}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Featured Decision Card ────────────────────────────────────────
function FeaturedDecisionCard({ colour, role, name, icon, tagline, bullets, status, cta, onActivate }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div style={{
      background: 'white',
      borderRadius: 16,
      overflow: 'hidden',
      boxShadow: '0 2px 20px rgba(0,0,0,0.08)',
      border: '1px solid rgba(0,0,0,0.06)',
      display: 'flex',
      alignItems: 'stretch',
    }}>
      <div style={{ width: 5, background: colour, flexShrink: 0 }} />

      <div style={{ padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', width: 360, flexShrink: 0, borderRight: '1px solid #f5f5f5' }}>
        <div style={{ flexShrink: 0 }}>{icon}</div>
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', background: colour + '12', border: `1px solid ${colour}30`, borderRadius: 20, padding: '0.2rem 0.7rem', marginBottom: '0.45rem' }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: colour, flexShrink: 0 }} />
            <span style={{ fontSize: '0.62rem', fontWeight: 700, color: colour, letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{role}</span>
          </div>
          <h2 style={{ fontSize: '1.35rem', fontWeight: 800, color: colour, margin: '0 0 0.25rem', lineHeight: 1.1 }}>{name}</h2>
          <p style={{ fontSize: '0.74rem', color: '#888', margin: 0, lineHeight: 1.5, fontStyle: 'italic' }}>{tagline}</p>
        </div>
      </div>

      <div style={{ flex: 1, padding: '1.25rem 1.75rem', display: 'flex', alignItems: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {bullets.map((b, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
              <div style={{ width: 5, height: 5, borderRadius: '50%', background: colour, flexShrink: 0, marginTop: 7 }} />
              <span style={{ fontSize: '0.78rem', color: '#444', lineHeight: 1.5 }}>
                {b.strong && <strong style={{ color: colour, fontWeight: 700 }}>{b.strong} </strong>}
                {b.rest}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '0.75rem', minWidth: 300, flexShrink: 0, borderLeft: '1px solid #f5f5f5' }}>
        {status && (
          <div style={{ background: colour + '08', borderRadius: 8, padding: '0.5rem 0.85rem' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: colour }}>{status.label}</div>
            {status.sub && <div style={{ fontSize: '0.62rem', color: '#aaa', marginTop: 2 }}>{status.sub}</div>}
          </div>
        )}
        <button
          onClick={onActivate}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{
            width: '100%', padding: '0.75rem',
            borderRadius: 10,
            border: `2px solid ${colour}`,
            background: hovered ? colour : 'white',
            color: hovered ? 'white' : colour,
            fontSize: '0.85rem', fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'inherit', transition: 'all 0.15s',
          }}>
          {cta}
        </button>
      </div>
    </div>
  );
}

// ── Guru card ─────────────────────────────────────────────────────
function GuruCard({ colour, role, name, icon, tagline, bullets, status, cta, onActivate, comingSoon, stepNumber }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div style={{
      background: 'white',
      borderRadius: 16,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      boxShadow: '0 2px 20px rgba(0,0,0,0.08)',
      opacity: comingSoon ? 0.75 : 1,
      position: 'relative',
      border: '1px solid rgba(0,0,0,0.06)',
      height: '100%',
    }}>
      {comingSoon ? (
        <div style={{
          background: colour, padding: '0.35rem 0.85rem',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
        }}>
          <span style={{ fontSize: '0.62rem', fontWeight: 800, color: 'white', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Coming soon</span>
          <span style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>Not yet available</span>
        </div>
      ) : (
        <div style={{ height: 5, background: colour, flexShrink: 0 }} />
      )}

      <div style={{ padding: '0.85rem 1.25rem 0', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {stepNumber && (
          <div style={{
            width: 22, height: 22, borderRadius: '50%',
            background: colour, color: 'white',
            fontSize: '0.65rem', fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            {stepNumber}
          </div>
        )}
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', background: colour + '12', border: `1px solid ${colour}30`, borderRadius: 20, padding: '0.2rem 0.7rem' }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: colour, flexShrink: 0 }} />
          <span style={{ fontSize: '0.62rem', fontWeight: 700, color: colour, letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{role}</span>
        </div>
      </div>

      <div style={{ padding: '1rem 1.25rem 0', display: 'flex', alignItems: 'flex-start', gap: '1rem', flexShrink: 0 }}>
        <div style={{ flexShrink: 0 }}>{icon}</div>
        <div style={{ flex: 1, paddingTop: '0.5rem' }}>
          <h2 style={{ fontSize: '1.35rem', fontWeight: 800, color: colour, margin: '0 0 0.3rem', lineHeight: 1.1 }}>{name}</h2>
          <p style={{ fontSize: '0.75rem', color: '#888', margin: 0, lineHeight: 1.5, fontStyle: 'italic' }}>{tagline}</p>
        </div>
      </div>

      <div style={{ margin: '1rem 1.25rem', height: 1, background: '#f2f2f2', flexShrink: 0 }} />

      <div style={{ padding: '0 1.25rem', flex: 1 }}>
        {bullets.map((b, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.55rem' }}>
            <div style={{ width: 5, height: 5, borderRadius: '50%', background: colour, flexShrink: 0, marginTop: 6 }} />
            <span style={{ fontSize: '0.78rem', color: '#444', lineHeight: 1.5 }}>
              {b.strong && <strong style={{ color: colour, fontWeight: 700 }}>{b.strong} </strong>}
              {b.rest}
            </span>
          </div>
        ))}
      </div>

      {status && (
        <div style={{ margin: '0.85rem 1.25rem 0', background: colour + '08', borderRadius: 8, padding: '0.55rem 0.85rem', display: 'flex', alignItems: 'center', gap: '0.6rem', flexShrink: 0 }}>
          {status.pct !== undefined && <ProgressRing pct={status.pct} colour={colour} size={34} />}
          <div>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: colour }}>{status.label}</div>
            {status.sub && <div style={{ fontSize: '0.62rem', color: '#aaa' }}>{status.sub}</div>}
          </div>
          {status.action && (
            <button onClick={status.action.fn}
              style={{ marginLeft: 'auto', fontSize: '0.62rem', fontWeight: 700, color: colour, background: 'none', border: `1px solid ${colour}44`, borderRadius: 20, padding: '2px 8px', cursor: 'pointer', width: 'auto', fontFamily: 'inherit' }}>
              {status.action.label}
            </button>
          )}
        </div>
      )}

      <div style={{ padding: '0.85rem 1.25rem 1.25rem', flexShrink: 0 }}>
        <button
          onClick={!comingSoon ? onActivate : undefined}
          disabled={comingSoon}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{
            width: '100%', padding: '0.8rem',
            borderRadius: 10,
            border: `2px solid ${comingSoon ? '#e0e0e0' : colour}`,
            background: hovered && !comingSoon ? colour : comingSoon ? '#fafafa' : 'white',
            color: hovered && !comingSoon ? 'white' : comingSoon ? '#bbb' : colour,
            fontSize: '0.85rem', fontWeight: 700,
            cursor: comingSoon ? 'default' : 'pointer',
            fontFamily: 'inherit', transition: 'all 0.15s',
          }}>
          {cta}
        </button>
      </div>
    </div>
  );
}

// ── Advisory card ─────────────────────────────────────────────────
function AdvisoryCard({ colour, role, name, icon, tagline, bullets, status, cta, onActivate, comingSoon }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div style={{
      background: 'white', borderRadius: 16, display: 'flex', flexDirection: 'column',
      overflow: 'hidden', boxShadow: '0 2px 20px rgba(0,0,0,0.08)',
      border: '1px solid rgba(0,0,0,0.06)', position: 'relative',
    }}>
      {comingSoon ? (
        <div style={{
          background: colour, padding: '0.35rem 0.85rem',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
        }}>
          <span style={{ fontSize: '0.62rem', fontWeight: 800, color: 'white', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Coming soon</span>
          <span style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>Not yet available</span>
        </div>
      ) : (
        <div style={{ height: 5, background: colour, flexShrink: 0 }} />
      )}

      <div style={{ padding: '0.85rem 1.25rem 0', flexShrink: 0 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', background: colour + '12', border: `1px solid ${colour}30`, borderRadius: 20, padding: '0.2rem 0.7rem' }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: colour, flexShrink: 0 }} />
          <span style={{ fontSize: '0.62rem', fontWeight: 700, color: colour, letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{role}</span>
        </div>
      </div>

      <div style={{ padding: '0.9rem 1.25rem 0', display: 'flex', alignItems: 'center', gap: '0.85rem', flexShrink: 0 }}>
        <div style={{ flexShrink: 0 }}>{icon}</div>
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 800, color: colour, margin: '0 0 0.25rem', lineHeight: 1.1 }}>{name}</h2>
          <p style={{ fontSize: '0.72rem', color: '#888', margin: 0, lineHeight: 1.5, fontStyle: 'italic' }}>{tagline}</p>
        </div>
      </div>

      {bullets && bullets.length > 0 && (
        <div style={{ padding: '0.75rem 1.25rem 0', flex: 1 }}>
          {bullets.map((b, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.4rem' }}>
              <div style={{ width: 5, height: 5, borderRadius: '50%', background: colour, flexShrink: 0, marginTop: 6 }} />
              <span style={{ fontSize: '0.75rem', color: '#444', lineHeight: 1.5 }}>
                {b.strong && <strong style={{ color: colour, fontWeight: 700 }}>{b.strong} </strong>}
                {b.rest}
              </span>
            </div>
          ))}
        </div>
      )}

      {status && (
        <div style={{ margin: '0.75rem 1.25rem 0', background: colour + '08', borderRadius: 8, padding: '0.5rem 0.85rem', flexShrink: 0 }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: colour }}>{status.label}</div>
          {status.sub && <div style={{ fontSize: '0.62rem', color: '#aaa', marginTop: 2 }}>{status.sub}</div>}
        </div>
      )}

      <div style={{ padding: '0.85rem 1.25rem 1.25rem', marginTop: 'auto', flexShrink: 0 }}>
        <button
          onClick={!comingSoon ? onActivate : undefined}
          disabled={comingSoon}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{
            width: '100%', padding: '0.7rem',
            borderRadius: 10,
            border: `2px solid ${comingSoon ? '#e0e0e0' : colour}`,
            background: hovered && !comingSoon ? colour : comingSoon ? '#fafafa' : 'white',
            color: hovered && !comingSoon ? 'white' : comingSoon ? '#bbb' : colour,
            fontSize: '0.82rem', fontWeight: 700,
            cursor: comingSoon ? 'default' : 'pointer',
            fontFamily: 'inherit', transition: 'all 0.15s',
          }}>
          {cta}
        </button>
      </div>
    </div>
  );
}

// ── Journey track components ───────────────────────────────────────
function TrackDot({ pct, colour, stepNumber, emphasis }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: '0.5rem' }}>
      <div style={{
        width: 44, height: 44, borderRadius: '50%',
        border: `2.5px solid ${colour}`,
        background: pct > 0 ? colour : 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: '0.4rem', position: 'relative', flexShrink: 0,
      }}>
        {pct > 0 ? (
          <span style={{ fontSize: '0.68rem', fontWeight: 800, color: 'white' }}>{pct}%</span>
        ) : (
          <span style={{ fontSize: '0.75rem', fontWeight: 800, color: colour, opacity: 0.4 }}>{stepNumber}</span>
        )}
        {pct === 100 && (
          <div style={{ position: 'absolute', top: -3, right: -3, width: 14, height: 14, borderRadius: '50%', background: colour, border: '2px solid white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: '0.45rem', color: 'white', fontWeight: 800 }}>✓</span>
          </div>
        )}
      </div>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: pct > 0 ? colour : '#bbb', letterSpacing: '0.04em', textAlign: 'center' }}>
        {emphasis}
      </div>
    </div>
  );
}

function TrackLine({ pct, colour }) {
  return (
    <div style={{ paddingTop: 20, display: 'flex', alignItems: 'flex-start' }}>
      <div style={{ position: 'relative', width: '100%', height: 3, background: '#e8e8f0', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${pct}%`, background: colour, transition: 'width 0.6s ease', borderRadius: 2 }} />
      </div>
    </div>
  );
}

function CardArrow() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', paddingTop: '3.25rem' }}>
      <div style={{ width: 0, height: 0, borderTop: '5px solid transparent', borderBottom: '5px solid transparent', borderLeft: '8px solid #d0d0d8' }} />
      <div style={{ fontSize: '0.5rem', color: '#c0c0cc', marginTop: 3, textAlign: 'center', lineHeight: 1.3 }}>feeds<br/>into</div>
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────
export default function Dashboard() {
  const navigate = useNavigate();
  const [got5Open, setGot5Open] = useState(false);
  const [websiteBannerDismissed, setWebsiteBannerDismissed] = useState(() =>
    !!localStorage.getItem('guruone_website_banner_dismissed')
  );
  const [websiteInferring, setWebsiteInferring] = useState(false);
  const [initiativeStats, setInitiativeStats] = useState(null);

  const user = (() => { try { return JSON.parse(localStorage.getItem('guruone_user')); } catch { return null; } })();
  const strategyData = loadStrategyData(user?.id);
  const sPct = calcStrategyPct(strategyData);
  const bPct = calcBlueprintPct();
  const decisionCount = getDecisionCount();
  const challenges = getChallenges();

  // Initiative health summary — fed to Got5Mins as one signal among several,
  // and used to drive the Delivery Guru track-dot completeness.
  // We compute on the client so the prompt is up to date the moment the panel opens.
  useEffect(() => {
    const userId = user?.id;
    if (!userId) { setInitiativeStats({ total: 0, overdue: 0, dueSoon: 0, stale: 0, done: 0 }); return; }
    let cancelled = false;
    axios.get(`${DELIVERY_API}/initiatives/${userId}`)
      .then(res => {
        if (cancelled) return;
        const inits = res.data?.initiatives || [];
        const todayISO = new Date().toISOString().slice(0, 10);
        const dueSoonCutoff = new Date(Date.now() + INITIATIVE_DUE_SOON_DAYS * 24 * 3600 * 1000).toISOString().slice(0, 10);
        const staleCutoffMs = Date.now() - INITIATIVE_STALE_DAYS * 24 * 3600 * 1000;
        const stats = {
          total: inits.length,
          overdue: 0,
          dueSoon: 0,
          stale: 0,
          done: 0,
        };
        for (const i of inits) {
          const isDone = i.status === 'done';
          if (isDone) { stats.done += 1; continue; }
          if (i.target_date && i.target_date < todayISO) stats.overdue += 1;
          else if (i.target_date && i.target_date <= dueSoonCutoff) stats.dueSoon += 1;
          const lastMs = i.last_status_update ? new Date(i.last_status_update).getTime() : NaN;
          if (!isFinite(lastMs) || lastMs < staleCutoffMs) stats.stale += 1;
        }
        setInitiativeStats(stats);
      })
      .catch(() => { if (!cancelled) setInitiativeStats({ total: 0, overdue: 0, dueSoon: 0, stale: 0, done: 0 }); });
    return () => { cancelled = true; };
  }, [user?.id]);

  const planReady = sPct >= 50 && bPct >= 50;
  const planPct = Math.min(sPct, bPct);
  const deliveryPct = calcDeliveryPct(initiativeStats?.total ?? 0);

  const greeting = user?.businessName
    ? `Welcome back — ${user.businessName}`
    : 'Welcome back';

  // Delivery Guru status — always accessible, but inform about auto-generation readiness
  const deliveryStatus = planReady
    ? { pct: 100, label: 'Ready to generate your plan', sub: 'Direction and Design complete enough to auto-generate' }
    : planPct > 0
      ? { pct: planPct, label: `${planPct}% ready for auto-generation`, sub: 'You can start planning now — complete more canvases to auto-generate' }
      : { label: 'Start with any initiative you are working on', sub: 'No canvas needed — or build your direction first for a richer plan' };

  return (
    <div style={{ minHeight: '100vh', background: '#f4f4f7' }}>
      <Header />

      <main style={{ maxWidth: 1320, margin: '0 auto', padding: '1.25rem 1.5rem' }}>

        {/* Greeting row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: '1rem' }}>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1a1a2e', margin: 0, letterSpacing: '-0.01em' }}>
              {greeting}
            </h1>
          </div>
          <Got5MinsTrigger onClick={() => setGot5Open(o => !o)} />
          {DELIVERY_ENABLED && (initiativeStats?.total ?? 0) > 0 && (
            <ReviewCycleWidget compact onClick={() => navigate('/delivery')} />
          )}
        </div>

        <Got5Mins
          strategyData={strategyData} challenges={challenges} sPct={sPct} bPct={bPct}
          initiativeStats={initiativeStats}
          open={got5Open} setOpen={setGot5Open}
        />

        {/* Website URL pre-populate banner */}
        {user?.websiteUrl && sPct === 0 && !websiteBannerDismissed && (
          <div style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #0f2744 100%)', borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'white', marginBottom: 2 }}>
                Pre-fill your canvas from your website
              </div>
              <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.4 }}>
                We will read <span style={{ color: '#1D9E75' }}>{user.websiteUrl}</span> and suggest values for both canvases — you review everything before saving.
              </div>
            </div>
            <button
              disabled={websiteInferring}
              onClick={async () => {
                setWebsiteInferring(true);
                try {
                  const res = await axios.post(`${CANVAS_API}/infer`, { url: user.websiteUrl });
                  if (res.data.canvas) {
                    const storageKey = user?.id ? `user_${user.id}` : 'default';
                    const existing = (() => { try { return JSON.parse(localStorage.getItem('guruone_strategy')) || {}; } catch { return {}; } })();
                    localStorage.setItem('guruone_strategy', JSON.stringify({ ...existing, [storageKey]: res.data.canvas }));
                    localStorage.setItem('guruone_website_banner_dismissed', '1');
                    setWebsiteBannerDismissed(true);
                    navigate('/strategy');
                  }
                } catch {}
                setWebsiteInferring(false);
              }}
              style={{ padding: '0.55rem 1rem', borderRadius: 8, background: '#1D9E75', color: 'white', border: 'none', fontSize: '0.78rem', fontWeight: 600, cursor: websiteInferring ? 'not-allowed' : 'pointer', fontFamily: 'inherit', width: 'auto', flexShrink: 0 }}>
              {websiteInferring ? 'Reading…' : 'Pre-fill canvases →'}
            </button>
            <button onClick={() => { localStorage.setItem('guruone_website_banner_dismissed', '1'); setWebsiteBannerDismissed(true); }}
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', fontSize: '0.8rem', fontFamily: 'inherit', width: 'auto', padding: '0.25rem', flexShrink: 0 }}>✕</button>
          </div>
        )}

        {/* ── DECISION ADVISOR ──────────────────────────────────── */}
        <div style={{ marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#aaa', marginBottom: '0.75rem' }}>
            Your decision advisor
          </div>
          <FeaturedDecisionCard
            colour="#1D9E75"
            role="Your virtual Non-Executive Director"
            name="Decision Guru"
            icon={<DecisionIcon />}
            tagline="Independent challenge and rigorous analysis on any decision you're facing"
            bullets={[
              { strong: 'Any stage', rest: '— use it before, during or after your canvases, at any point in your journey' },
              { strong: 'Any topic', rest: "— people, pricing, market entry, operations, finance or anything you're wrestling with" },
              { strong: 'Any scale', rest: '— a quick gut-check or a fully structured deliberation with criteria weighting' },
            ]}
            status={decisionCount > 0 ? {
              label: `${decisionCount} decision${decisionCount !== 1 ? 's' : ''} analysed`,
              sub: 'Decisions inform your canvas automatically',
            } : {
              label: 'No decisions yet',
              sub: "Start with any challenge you're facing",
            }}
            cta="Work through a decision →"
            onActivate={() => { track('guru_opened', { guru: 'Decision' }); navigate('/decision'); }}
          />
        </div>

        {/* ── STRATEGY JOURNEY ──────────────────────────────────── */}
        <div style={{ marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#aaa', marginBottom: '0.75rem' }}>
            Your strategy journey
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 40px 1fr 40px 1fr', alignItems: 'stretch' }}>

            <TrackDot pct={sPct} colour="#7C3AED" stepNumber={1} emphasis="Direction" />
            <TrackLine pct={sPct} colour="#7C3AED" />
            <TrackDot pct={bPct} colour="#185FA5" stepNumber={2} emphasis="Design" />
            <TrackLine pct={bPct} colour="#185FA5" />
            <TrackDot pct={deliveryPct} colour="#D97706" stepNumber={3} emphasis="Delivery" />

            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <GuruCard
                stepNumber={1} colour="#7C3AED"
                role="Your virtual Chief Strategy Officer"
                name="Direction Guru" icon={<DirectionIcon />}
                tagline="Clarify your why, state where you're going and define how you'll win!"
                bullets={[
                  { strong: 'Why', rest: '— your purpose and what you want from the business' },
                  { strong: 'What', rest: '— your value proposition and how you compete' },
                  { strong: 'Where', rest: '— your vision and the strategy to get there' },
                ]}
                status={{
                  pct: sPct,
                  label: sPct === 0 ? 'Not started' : sPct === 100 ? 'Direction complete ✓' : `${sPct}% complete`,
                  sub: sPct > 0 ? `${Math.round(sPct * 12 / 100)} of 12 sections done` : 'Start with Founder Purpose',
                  action: sPct > 0 ? { label: 'View one page →', fn: () => navigate('/one-page-strategy') } : null,
                }}
                cta={sPct === 0 ? 'Start my direction →' : 'Continue my direction →'}
                onActivate={() => { track('guru_opened', { guru: 'Direction' }); navigate('/strategy'); }}
              />
            </div>

            <CardArrow />

            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <GuruCard
                stepNumber={2} colour="#185FA5"
                role="Your virtual Chief Operating Officer"
                name="Design Guru" icon={<DesignIcon />}
                tagline="Map how you operate today and design the business your direction demands"
                bullets={[
                  { strong: 'How', rest: '— your business operates today, an honest current state' },
                  { strong: 'What', rest: '— it needs to look like to deliver your strategy' },
                  { strong: 'Where', rest: '— the biggest gaps and changes are' },
                ]}
                status={{
                  pct: bPct,
                  label: bPct === 0 ? 'Not started' : `${bPct}% of current state mapped`,
                  sub: bPct > 0
                    ? sPct >= 40 ? 'Ready to generate future state →' : 'Complete more direction first'
                    : 'Start by mapping how you operate today',
                }}
                cta={bPct === 0 ? 'Design my future business →' : 'Continue Design Guru →'}
                onActivate={() => { track('guru_opened', { guru: 'Design' }); navigate('/operating-model'); }}
              />
            </div>

            <CardArrow />

            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <GuruCard
                stepNumber={3} colour="#D97706"
                role="Your virtual Chief of Staff"
                name="Delivery Guru" icon={<DeliveryIcon />}
                tagline="Turn your direction and design into a sequenced, prioritised plan of action"
                bullets={[
                  { strong: 'How', rest: '— to achieve your direction and design, initiative by initiative' },
                  { strong: 'When', rest: '— to do what, phased and sequenced correctly' },
                  { strong: 'Who', rest: '— needs to do what to make it happen' },
                ]}
                status={DELIVERY_ENABLED
                  ? deliveryStatus
                  : { label: 'Coming soon', sub: 'Being reworked for the next release' }}
                cta={DELIVERY_ENABLED
                  ? (sPct === 0 && bPct === 0 ? 'Start planning →' : planReady ? 'Generate my delivery plan →' : 'Go to Delivery Guru →')
                  : 'Coming soon'}
                onActivate={() => { track('guru_opened', { guru: 'Delivery' }); navigate('/delivery'); }}
                comingSoon={!DELIVERY_ENABLED}
              />
            </div>

          </div>
        </div>

        {/* ── YOUR FULL C-SUITE ─────────────────────────────────── */}
        <div>
          <div style={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#aaa', marginBottom: '0.75rem' }}>
            Your full C-suite
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>

            <AdvisoryCard
              colour="#BA7517"
              role="Your virtual Chief Financial Officer"
              name="Finance Guru"
              icon={<FinanceIcon />}
              tagline="Financial health, cash flow and commercial decision support"
              bullets={[
                { strong: 'Cash', rest: '— runway, burn rate and working capital health' },
                { strong: 'Margin', rest: '— where money is made and where it is lost' },
                { strong: 'Commercial', rest: '— pricing, investment and financial planning' },
              ]}
              status={{ label: 'Coming soon', sub: 'Financial advisory across all stages' }}
              cta="Coming soon"
              onActivate={() => {}}
              comingSoon
            />

            <AdvisoryCard
              colour="#7B3AC2"
              role="Your virtual Chief Marketing Officer"
              name="Marketing Guru"
              icon={<MarketingIcon />}
              tagline="Positioning, channels and customer acquisition strategy"
              bullets={[
                { strong: 'Positioning', rest: '— how you show up and what makes you distinctive' },
                { strong: 'Channels', rest: '— acquisition strategy and media mix' },
                { strong: 'Brand', rest: '— message, tone and how to reach the right people' },
              ]}
              status={{ label: 'Coming soon', sub: 'Marketing advisory across all stages' }}
              cta="Coming soon"
              onActivate={() => {}}
              comingSoon
            />

            <AdvisoryCard
              colour="#C0442B"
              role="Your virtual Chief Revenue Officer"
              name="Sales Guru"
              icon={<SalesIcon />}
              tagline="Pipeline, conversion and revenue growth across your business"
              bullets={[
                { strong: 'Pipeline', rest: '— opportunities at each stage, velocity and conversion' },
                { strong: 'Revenue', rest: '— forecasting, target-setting and growth levers' },
                { strong: 'Acquisition', rest: '— new customer channels and market penetration' },
              ]}
              status={{ label: 'Coming soon', sub: 'Sales advisory across all stages' }}
              cta="Coming soon"
              onActivate={() => {}}
              comingSoon
            />

            <AdvisoryCard
              colour="#0D9488"
              role="Your virtual Chief People Officer"
              name="People Guru"
              icon={<PeopleIcon />}
              tagline="Team structure, culture and capability to support your direction"
              bullets={[
                { strong: 'Team', rest: '— structure, capacity and the right people in the right roles' },
                { strong: 'Culture', rest: '— how people work together and what you want to protect' },
                { strong: 'Capability', rest: '— skills gaps, development and retention' },
              ]}
              status={{ label: 'Coming soon', sub: 'People advisory across all stages' }}
              cta="Coming soon"
              onActivate={() => {}}
              comingSoon
            />

            <AdvisoryCard
              colour="#0891B2"
              role="Your virtual Chief Technology Officer"
              name="Technology Guru"
              icon={<TechnologyIcon />}
              tagline="Technology, systems and digital infrastructure to scale your business"
              bullets={[
                { strong: 'Systems', rest: '— what you run on, where the gaps are and what needs fixing' },
                { strong: 'Digital', rest: '— online presence, automation and productivity tools' },
                { strong: 'Scale', rest: '— infrastructure decisions as you grow' },
              ]}
              status={{ label: 'Coming soon', sub: 'Technology advisory across all stages' }}
              cta="Coming soon"
              onActivate={() => {}}
              comingSoon
            />

            <div style={{
              background: 'white', borderRadius: 16, display: 'flex', flexDirection: 'column',
              overflow: 'hidden', boxShadow: '0 2px 20px rgba(0,0,0,0.06)',
              border: '1px solid rgba(0,0,0,0.06)',
            }}>
              <div style={{
                background: '#9ca3af', padding: '0.35rem 0.85rem',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
              }}>
                <span style={{ fontSize: '0.62rem', fontWeight: 800, color: 'white', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Coming soon</span>
                <span style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>Not yet available</span>
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem 1.5rem', gap: '0.6rem' }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', color: '#9ca3af' }}>+</div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#6b7280', marginBottom: '0.25rem' }}>More Gurus coming</div>
                  <div style={{ fontSize: '0.72rem', color: '#9ca3af', lineHeight: 1.5 }}>Additional specialists are in development and will be added to your C-suite as they launch</div>
                </div>
              </div>
            </div>

          </div>
        </div>

      </main>
    </div>
  );
}
